import { z } from 'zod';

/**
 * Academic calendar (rentrée, vacances, examens, délibérations, soutenances…). Kinds are fixed categories
 * (colour and icon in the UI); the title is free text written by the school.
 */
export const CALENDAR_EVENT_KINDS = [
  'start',
  'holiday',
  'exams',
  'deliberation',
  'defense',
  'deadline',
  'event',
] as const;
export type CalendarEventKind = (typeof CALENDAR_EVENT_KINDS)[number];

/** Who sees the event: everyone, students only (student app) or staff and teachers only (back-office). */
export const CALENDAR_AUDIENCES = ['all', 'students', 'staff'] as const;
export type CalendarAudience = (typeof CALENDAR_AUDIENCES)[number];

export const CalendarEventInputSchema = z
  .object({
    academicYearId: z.uuid(),
    kind: z.enum(CALENDAR_EVENT_KINDS),
    title: z.string().trim().min(2, 'calendar.errors.title').max(140),
    startsOn: z.iso.date('calendar.errors.date'),
    endsOn: z.iso.date('calendar.errors.date'),
    audience: z.enum(CALENDAR_AUDIENCES).default('all'),
    description: z.string().trim().max(500).nullable().optional(),
  })
  .refine((e) => e.endsOn >= e.startsOn, { path: ['endsOn'], message: 'calendar.errors.range' });
export type CalendarEventInput = z.input<typeof CalendarEventInputSchema>;

export const CalendarEventSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  kind: z.enum(CALENDAR_EVENT_KINDS),
  title: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  audience: z.enum(CALENDAR_AUDIENCES),
  description: z.string().nullable(),
});
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;

export const CalendarQuerySchema = z.object({ yearId: z.uuid().optional() });
